import { Global, css } from '@emotion/react';
import { Box, cssVar, useSize } from '@hover/blueprint';
import type { ReactNode } from 'react';
import React, { createContext, useEffect, useRef, useState } from 'react';

import { isBrowser } from 'src/shared/helpers/environment';

export interface ViewportSizeContext {
  height?: string;
}

export const Context = createContext<ViewportSizeContext>({});

const $height = cssVar('viewport-size-provider-height');
const contextValue = { height: $height.reference };

export interface ViewportSizeProviderProps {
  children: ReactNode;
  root?: string;
}

const cssSupports = (property: string, value: string) =>
  isBrowser() &&
  typeof CSS !== 'undefined' &&
  typeof CSS.supports === 'function'
    ? CSS.supports(property, value)
    : false;

const useSupportsStyle = (property: string, value: string) => {
  const [isSupported, setIsSupported] = useState(cssSupports(property, value));

  useEffect(
    () => setIsSupported(cssSupports(property, value)),
    [property, value],
  );

  return isSupported;
};

/**
 * Provides a height value that behaves similar to `100dvh` for browsers that
 * don't support "dynamic viewport percentage" units natively. On browsers that
 * do support `dvh`, this simply provides a CSS variable with the `100dvh`
 * value.
 *
 * The height value responds to any browser interfaces that dynamically resize
 * (Safari on mobile is notorious for this). The returned height _does not_
 * behave like `100vh` which fails to account for the browser interface.
 * Chakra's `var(--chakra-vh)` polyfill that accomplishes the same thing is
 * missing some key browser support Specifically, the android emulator browser
 * and the Cypress docker image browser (Chrome 107) do not support it.
 *
 * The full height provider renders a fixed full screen box in the background to
 * measure the full screen height and uses a `ResizeObserver` to update the value
 * when the browser window is resized.
 */
const ViewportSizeProvider = ({
  children,
  root = ':root, :host',
}: ViewportSizeProviderProps) => {
  const sizeRef = useRef<HTMLDivElement>(null);
  const size = useSize(sizeRef);

  const isDynamicHeightSupported = useSupportsStyle('height', '100dvh');

  return (
    <Context.Provider value={contextValue}>
      <Global
        styles={css({
          [root]: { [$height.variable]: size ? `${size?.height}px` : '100vh' },
          '@supports (height: 100dvh)': {
            [root]: { [$height.variable]: '100dvh' },
          },
        })}
      />
      <Box
        bottom={0}
        display="hidden"
        left={0}
        position="fixed"
        ref={isDynamicHeightSupported ? undefined : sizeRef}
        right={0}
        top={0}
        zIndex="hide"
      />
      {children}
    </Context.Provider>
  );
};

export { ViewportSizeProvider };
